<template>
  <v-container fluid>
    <PageHeading :text="'Категории страниц помощи'">
      <template v-slot:[`toolbar.prepend`]>
        <v-btn
          link
          small
          outlined
          color="primary"
          class="mx-2 mt-2"
          :disabled="isMutationLoading"
          @click="newCategoryHandler()"
        >
          <v-icon class="mr-2" small> mdi-plus </v-icon>
          Создать категорию
        </v-btn>
      </template>
    </PageHeading>
    <v-row>
      <v-col v-if="loadingData" class="wrapper">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
      <v-col v-else>
        <PageContent>
          <TreeInfoCategories
            :data="infoCategories"
            :loading="loadingData"
            @new-category-children="newCategoryHandler($event)"
            @remove-category="deleteInfoCategory($event)"
            @edit-category="editCategoryHandler($event)"
          />
        </PageContent>
      </v-col>
    </v-row>
    <FormDialog
      v-if="infoCategory"
      :data="infoCategory"
      :id="parentCategoryId"
      :info-categories="infoCategories"
      :language="language"
      :loading="loadingData"
      :disabled="isMutationLoading"
      @submit="btnFormClick"
      @close="formDialogCloseHandler"
      @language-update="language = $event"
    />
    <ConfirmationDialog ref="confirmInfoCategoryRemoving" confirmText="Удалить" />
  </v-container>
</template>

<script>
import { contentLocales, contentLocalesValues } from '@/configs/locales'
import { getListWithNewMainLanguage, setTranslation } from '@/utils/locales'

import TreeInfoCategories from './tree'
import PageHeading from '@/components/page-heading'
import FormDialog from './form-dialog'
import ConfirmationDialog from '@/components/confirmation-dialog'
import infoCategoriesQuery from '@/services/gql/types/InfoCategories/queries/infoCategories.gql'
import createInfoCategory from '@/services/gql/types/InfoCategories/mutations/createInfoCategory.gql'
import deleteInfoCategory from '@/services/gql/types/InfoCategories/mutations/deleteInfoCategory.gql'
import updateInfoCategory from '@/services/gql/types/InfoCategories/mutations/updateInfoCategory.gql'

import infoCategoryQuery from '@/services/gql/types/InfoCategories/queries/infoCategory.gql'
import PageContent from '@/components/page-content/PageContent.vue'

export default {
  name: 'PageInfoCategories',
  metaInfo () {
    return {
      title: 'Категории статей помощи'
    }
  },
  components: {
    PageContent,
    PageHeading,
    TreeInfoCategories,
    ConfirmationDialog,
    FormDialog
  },
  data () {
    return {
      infoCategories: null,
      isNew: false,
      infoCategory: null,
      parentCategoryId: null,
      language: contentLocales[0].value
    }
  },
  computed: {
    loadingData () {
      return this.$apollo.queries.infoCategories.loading
    },
    isMutationLoading () {
      return (
        this.$async.createInfoCategory.$pending || this.$async.updateInfoCategory.$pending
      )
    },
    returnInfoCategoryId () {
      return this.infoCategory?.id ? Number(this.infoCategory.id) : null
    }
  },
  methods: {
    async btnFormClick (payload) {
      if (Object.keys(this.infoCategory).length > 0) {
        await this.updateInfoCategory(payload)
      } else {
        await this.createInfoCategory(payload)
      }
    },
    async formDialogCloseHandler () {
      this.language = contentLocales[0].value
      this.resetCategoryAndParent()
      await this.$apollo.queries.infoCategories.refresh()
    },
    resetCategoryAndParent () {
      this.parentCategoryId = null
      this.infoCategory = null
    },
    newCategoryHandler (parentCategoryId = null) {
      this.resetCategoryAndParent()
      this.infoCategory = {}
      this.parentCategoryId = parentCategoryId
    },
    async editCategoryHandler (category) {
      this.infoCategory = category
      await this.$apollo.queries.infoCategory.refetch()
    },
    async createInfoCategory (data) {
      delete data.translations
      try {
        await this.$async.createInfoCategory.$perform({
          data
        })
        this.$successMsg('Категория страницы помощи создана')
        this.resetCategoryAndParent()
        this.$apollo.queries.infoCategories.refresh()
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, 'Ошибка при создании страницы помощи')
      }
    },
    async deleteInfoCategory (id) {
      try {
        await this.$refs.confirmInfoCategoryRemoving.open()
        await this.$async.deleteInfoCategory.$perform({ id: Number(id) })
        this.$successMsg('Категория страницы помощи удалена')
        this.$apollo.queries.infoCategories.refresh()
      } catch (e) {
        this.$handleApiError(e.graphQLErrors, `Ошибка при удалении гида с ID ${this.id}`)
      }
    },
    async updateInfoCategory (data) {
      try {
        setTranslation(data, this.language)
        await this.$async.updateInfoCategory.$perform(
          {
            id: Number(this.infoCategory.id),
            data
          },
          { Language: getListWithNewMainLanguage(this.language, contentLocalesValues()) }
        )
        this.$successMsg('Категория статьи помощи изменена')
        // this.resetCategoryAndParent()
        // this.$apollo.queries.infoCategories.refresh()
      } catch (e) {
        this.$handleApiError(
          e,
          `Ошибка при редактировании категории статьи помощи с ID ${this.infoCategory.id}`
        )
      }
    }
  },
  apollo: {
    infoCategories: {
      query: infoCategoriesQuery,
      fetchPolicy: 'network-only',
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      result (res) {
        this.infoCategories =
          res?.data?.infoCategories?.list?.map(category => ({
            ...category,
            // for nested parent - child logic in the form
            children: [],
            parentId: category?.parentCategory?.id || null
          })) || null
      }
    },
    infoCategory: {
      query: infoCategoryQuery,
      fetchPolicy: 'network-only',
      skip () {
        return !this.returnInfoCategoryId
      },
      context () {
        return {
          headers: {
            Language: getListWithNewMainLanguage(this.language, contentLocalesValues())
          }
        }
      },
      variables () {
        return {
          where: {
            id: this.returnInfoCategoryId
          }
        }
      }
    }
  },
  async: {
    createInfoCategory (variables) {
      return this.$apollo.mutate({
        mutation: createInfoCategory,
        variables
      })
    },
    deleteInfoCategory (variables) {
      return this.$apollo.mutate({
        mutation: deleteInfoCategory,
        variables
      })
    },
    updateInfoCategory (variables, headers) {
      return this.$apollo.mutate({
        mutation: updateInfoCategory,
        variables,
        context: {
          headers
        }
      })
    }
  }
}
</script>
<style>
.wrapper {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
